import {omit, flowRight} from 'lodash';
import {fromJS, List, Map, OrderedSet, Set} from 'immutable';
import qs from 'qs';

import {getPluralForm} from '@albert-io/json-api-framework/models/registry/resources';

// Imported from the neutral modelResolverRef (not resources/modelRegistry directly) to break the
// mandark.resource <-> modelRegistry import cycle. modelRegistry injects the implementations via
// setModelResolver when it loads (see resources/modelResolverRef).
import {getModel, getCollectionModel} from 'resources/modelResolverRef';
import {getRequestAgent} from '@albert-io/mandark/authentication/requestAgent';
import {getMandarkURL, isBrowser} from '@albert-io/environment';

import {getQueryRegistryInterface} from 'lib/activeQueryRegistry';
import {handleMandarkErrorResponse} from 'resources/mandark.errorHandler';

export {handleMandarkErrorResponse} from 'resources/mandark.errorHandler';

export type ResourceQueryConfigurationType = {
  resourcePath: Array<string>;
  filter: Record<string, unknown>;
  include: string;
  customQuery: Record<string, unknown>;
  customHeader: Record<string, unknown>;
};

export type ResourceType = Map<unknown, unknown>;
export type CollectionType = List<ResourceType>;
export type APIRequestType = Promise<CollectionType | ResourceType | null | undefined>;

/**
 * The model resolver (resources/modelResolverRef) hands back JSON:API model classes as `unknown`
 * (it imports nothing to avoid an import cycle). Within this engine those values are used as
 * constructors via `new Model(...)`, so we narrow them to this constructor shape locally.
 */
type ModelConstructor = new (...args: unknown[]) => unknown;

let INTEREST_REGISTRY = Map<string, Map<string, unknown>>();
let STATUS_CACHE = Map<string, unknown>();
let ACTIVE_PENDING_REQUESTS = Map<string, Promise<unknown>>();
/**
 * The `DISPATCHED_QUERY_REGISTRY` is a registry of all quereies made in a
 * session, organized by resource. This allows us to invalidate "partially"
 * by invalidating resources at various points in the tree.
 *
 * @see `invalidatePartialInterest`
 */
let DISPATCHED_QUERY_REGISTRY = getQueryRegistryInterface();
let PENDING_INVALIDATIONS = Map<string, () => void>();
let SERVER_PAYLOAD_REGISTRY = Map<string, unknown>();
let INVALIDATION_FROZEN = false;

const DEFAULT_HEADER = {
  'Content-Type': 'application/vnd.api+json'
};

export const BUILD_HEADER = (customHeader = {}) => {
  const headers = {
    ...DEFAULT_HEADER,
    ...customHeader
  };

  if (isBrowser()) {
    headers['Albert-App-URL'] = global.window.location.pathname;
    if (process.env.STATIC_ASSETS_APP_PATH) {
      // will break for local builds
      headers['Albert-App-Version'] = process.env.STATIC_ASSETS_APP_PATH.split(
        '/app/release/'
      )[1].replace(/\//g, '');
    }
  }

  return headers;
};

let ERROR_HANDLER: (...args: unknown[]) => unknown = () => {};
let RESPONSE_HANDLER: (res: unknown) => unknown = (res) => res;

const CACHE_CONFIG = Map({
  defaultTimeToLive: 5 * 60 * 1000,
  requestTimeout: 60 * 1000,
  requestWarnCountThreshold: 20,
  requestWarnTimePeriod: 5000
});

let REQUEST_COUNT = 0;

export function getCachesForClient(): {
  DISPATCHED_QUERY_REGISTRY: Record<string, unknown>;
  SERVER_PAYLOAD_REGISTRY: Record<string, unknown>;
} {
  return {
    DISPATCHED_QUERY_REGISTRY: DISPATCHED_QUERY_REGISTRY.getRegistryContents().toJS(),
    SERVER_PAYLOAD_REGISTRY: SERVER_PAYLOAD_REGISTRY.toJS()
  };
}

setInterval(() => {
  const REQUEST_THRESHOLD = CACHE_CONFIG.get('requestWarnCountThreshold');
  const THRESHOLD_PERIOD = CACHE_CONFIG.get('requestWarnTimePeriod');

  if (REQUEST_COUNT > REQUEST_THRESHOLD) {
    logger.error(
      'Warning! Your request count of %d has exceeded %d requests in %d!',
      REQUEST_COUNT,
      REQUEST_THRESHOLD,
      THRESHOLD_PERIOD
    );
  }

  REQUEST_COUNT = 0;
}, CACHE_CONFIG.get('requestWarnTimePeriod'));

let CHANGE_CALLBACK = () => {};

// ------------ PUBLIC FUNCTIONS -------------
export function freezeInvalidation() {
  INVALIDATION_FROZEN = true;
}

export function resumeInvalidation() {
  INVALIDATION_FROZEN = false;
  PENDING_INVALIDATIONS.forEach((request) => request?.());
  PENDING_INVALIDATIONS = Map<string, () => void>();
}

/**
 * @param root0
 * @param root0.wipeErrorHandler
 * @memberOf Server.Isomorphism
 */
export function resetCache({
  wipeErrorHandler = true
}: {
  wipeErrorHandler?: boolean;
} = {}) {
  INTEREST_REGISTRY = Map<string, Map<string, unknown>>();
  STATUS_CACHE = Map<string, unknown>();
  ACTIVE_PENDING_REQUESTS = Map<string, Promise<unknown>>();
  SERVER_PAYLOAD_REGISTRY = Map<string, unknown>();
  DISPATCHED_QUERY_REGISTRY = getQueryRegistryInterface();
  if (wipeErrorHandler) {
    ERROR_HANDLER = () => {};
  }
}

export function getHeaders(): Record<string, unknown> {
  return BUILD_HEADER();
}

export function setChangeCallback(callback: (...args: unknown[]) => unknown) {
  CHANGE_CALLBACK = callback;
}

export function getActivePendingRequests(): Array<Promise<unknown>> {
  return ACTIVE_PENDING_REQUESTS.toArray();
}

export function registerErrorHandler(callback: (...args: unknown[]) => unknown) {
  ERROR_HANDLER = callback;
}

export function registerResponseHandler(callback: (res: unknown) => unknown) {
  RESPONSE_HANDLER = flowRight(callback, RESPONSE_HANDLER);
}

export function getVersionAgnosticResourceType(type: string): string {
  return type.replace(/_v\d+$/, '');
}

export function getResource({
  resourcePath,
  filter,
  include,
  customQuery,
  customHeader
}: ResourceQueryConfigurationType): ResourceType | CollectionType {
  const endpointPath = buildEndpoint({resourcePath, filter, include, customQuery});
  const resourceData = INTEREST_REGISTRY.getIn([endpointPath, 'responseData'], Map());
  const statusData = STATUS_CACHE.getIn(resourcePath, Map());

  if (filter || include || customQuery || resourceData.isEmpty()) {
    if (INTEREST_REGISTRY.has(endpointPath)) {
      _refreshCache(endpointPath);
      return INTEREST_REGISTRY.get(endpointPath).get('responseData', Map()) as
        | ResourceType
        | CollectionType;
    }
    _registerInterest(endpointPath, resourcePath, customHeader);
    DISPATCHED_QUERY_REGISTRY = DISPATCHED_QUERY_REGISTRY.addInterestKey(
      {
        customHeader,
        customQuery,
        filter,
        resourcePath
      },
      endpointPath
    );
    return Map();
  }
  if (statusData.get('__dirty', false)) {
    const attachedInterests = STATUS_CACHE.getIn(
      [...resourcePath, '__attachedInterests'],
      OrderedSet()
    );
    if (INTEREST_REGISTRY.has(endpointPath)) {
      _refreshCache(statusData.get('__interestPath'));
    } else if (!attachedInterests.isEmpty()) {
      _refreshCache(attachedInterests.last(), true);
    } else {
      _registerInterest(endpointPath, resourcePath, customHeader);
      DISPATCHED_QUERY_REGISTRY = DISPATCHED_QUERY_REGISTRY.addInterestKey(
        {
          customHeader,
          customQuery,
          filter,
          resourcePath
        },
        endpointPath
      );
    }

    STATUS_CACHE = STATUS_CACHE.setIn([...resourcePath, '__dirty'], false);
    return resourceData;
  }
  if (INTEREST_REGISTRY.has(endpointPath)) {
    _refreshCache(endpointPath);
  }
  return resourceData;
}

/**
 * Retrive the `error` property of a resource from the `INTEREST_REGISTRY`.
 *
 * This method will **NOT** make a request for the resource if you have not already.
 *
 * @param root0
 * @param root0.resourcePath
 * @param root0.filter
 * @param root0.include
 * @param root0.customQuery
 * @param root0.customHeader
 * @returns {Error|null}
 */
export function getResourceError({
  resourcePath,
  filter,
  include,
  customQuery,
  customHeader: _customHeader
}: ResourceQueryConfigurationType): Error | null {
  const endpointPath = buildEndpoint({resourcePath, filter, include, customQuery});
  if (INTEREST_REGISTRY.has(endpointPath) === false) {
    return null;
  }
  return INTEREST_REGISTRY.get(endpointPath).get('error', null) as Error | null;
}

export async function getResourcePromise({
  resourcePath,
  filter,
  include,
  customQuery,
  customHeader: _customHeader
}: ResourceQueryConfigurationType): Promise<unknown> {
  /**
   * Using 'arguments' only works while there are no defaults for the destructed argument... careful.
   */
  const resource = arguments[0];
  if (isResourceReady(resource)) {
    return getResource(resource);
  }

  const requestId = generateRequestId(buildEndpoint({resourcePath, filter, include, customQuery}));

  await ACTIVE_PENDING_REQUESTS.get(requestId, Promise.resolve());

  return getResource(resource);
}

export function getResourceMetadata({
  resourcePath,
  filter,
  include,
  customQuery,
  customHeader: _customHeader
}: ResourceQueryConfigurationType): Map<string, unknown> {
  const endpointPath = buildEndpoint({resourcePath, filter, include, customQuery});
  return INTEREST_REGISTRY.getIn([endpointPath, 'meta'], Map());
}

export function invalidateModel(modelPath: Array<string>) {
  const attachedInterests = STATUS_CACHE.getIn([...modelPath, '__attachedInterests'], OrderedSet());
  attachedInterests.forEach((interestId) => invalidateInterestById(interestId, false));
  setTimeout(CHANGE_CALLBACK, 0);
}

export function invalidatePartialInterest(query: Record<string, unknown>) {
  // Strip the model in case the query object has been built with `resource()`
  const sanitizedQuery = omit(query, ['Model', 'defaultModel']);
  let invalidationCount = 0;
  DISPATCHED_QUERY_REGISTRY.getInvalidationKeys(sanitizedQuery).forEach((key) => {
    if (key === undefined) {
      return;
    }
    if (INTEREST_REGISTRY.has(key)) {
      invalidationCount++;
      STATUS_CACHE = STATUS_CACHE.setIn([key, '__dirty'], true);
      STATUS_CACHE.getIn([key, '__derivedResources'], Set()).forEach((derivedInterest) => {
        STATUS_CACHE = STATUS_CACHE.setIn([...derivedInterest, '__dirty'], true);
      });
      if (ACTIVE_PENDING_REQUESTS.has(key)) {
        ACTIVE_PENDING_REQUESTS.get(key).then(
          () => (STATUS_CACHE = STATUS_CACHE.setIn([key, '__dirty'], true))
        );
      }
    }
  });
  setTimeout(CHANGE_CALLBACK, 0);
  logger.silly('Partial interest invalidation: %d queries invalidated.', invalidationCount);
}

export function invalidateInterest({
  resourcePath,
  filter,
  include,
  customQuery,
  customHeader
}: {
  resourcePath: Array<string>;
  filter: Record<string, unknown>;
  include: string;
  customQuery: Record<string, unknown>;
  customHeader: Record<string, unknown>;
}) {
  const endpointPath = buildEndpoint({resourcePath, filter, include, customQuery});
  if (resourcePath.length > 1) {
    STATUS_CACHE = STATUS_CACHE.setIn([...resourcePath, '__dirty'], true);
    const attachedInterests = STATUS_CACHE.getIn([...resourcePath, '__attachedInterests'], List());
    attachedInterests.forEach((key) => {
      STATUS_CACHE = STATUS_CACHE.setIn([key, '__dirty'], true);
    });
  }

  if (filter || include || customQuery || customHeader) {
    STATUS_CACHE = STATUS_CACHE.setIn([endpointPath, '__dirty'], true);
    STATUS_CACHE.getIn([endpointPath, '__derivedResources'], Set()).forEach((derivedInterest) => {
      STATUS_CACHE = STATUS_CACHE.setIn([...derivedInterest, '__dirty'], true);
    });
  }

  if (ACTIVE_PENDING_REQUESTS.has(endpointPath)) {
    ACTIVE_PENDING_REQUESTS.get(endpointPath).then(() => {
      STATUS_CACHE = STATUS_CACHE.setIn([endpointPath, '__dirty'], true);
    });
  }
  setTimeout(CHANGE_CALLBACK, 0);
}

export function invalidateInterestById(id: string, shouldTriggerChange = true) {
  STATUS_CACHE = STATUS_CACHE.setIn([id, '__dirty'], true);
  STATUS_CACHE.getIn([id, '__derivedResources'], Set()).forEach((derivedInterest) => {
    STATUS_CACHE = STATUS_CACHE.setIn([...derivedInterest, '__dirty'], true);
  });
  if (ACTIVE_PENDING_REQUESTS.has(id)) {
    ACTIVE_PENDING_REQUESTS.get(id).then(() => {
      STATUS_CACHE = STATUS_CACHE.setIn([id, '__dirty'], true);
    });
  }
  if (shouldTriggerChange) {
    setTimeout(CHANGE_CALLBACK, 0);
  }
}

export function isResourcePending({
  resourcePath,
  filter,
  include,
  customQuery,
  customHeader
}: ResourceQueryConfigurationType): boolean {
  const statusData = STATUS_CACHE.getIn(resourcePath, Map());
  getResource({
    customHeader,
    customQuery,
    filter,
    include,
    resourcePath
  }); // If we don't have it, initialize it
  const endpointPath = buildEndpoint({resourcePath, filter, include, customQuery});
  return (
    INTEREST_REGISTRY.getIn([endpointPath, 'status']) === 'pending' ||
    statusData.get('__dirty', false) === true
  );
}

export function isResourceReady({
  resourcePath,
  filter,
  include,
  customQuery,
  customHeader
}: ResourceQueryConfigurationType): boolean {
  const endpointPath = buildEndpoint({resourcePath, filter, include, customQuery});
  getResource({
    customHeader,
    customQuery,
    filter,
    include,
    resourcePath
  }); // If we don't have it, initialize it
  const isRequestPending = ACTIVE_PENDING_REQUESTS.has(endpointPath);

  if (INTEREST_REGISTRY.has(endpointPath)) {
    return !STATUS_CACHE.getIn([endpointPath, '__dirty'], false) && !isRequestPending;
  }
  return !STATUS_CACHE.getIn([...resourcePath, '__dirty'], false);
}

export function isResourcePopulated({
  resourcePath,
  filter,
  include,
  customQuery,
  customHeader
}: ResourceQueryConfigurationType): boolean {
  const resource = getResource({
    customHeader,
    customQuery,
    filter,
    include,
    resourcePath
  });
  const endpointPath = buildEndpoint({resourcePath, filter, include, customQuery});
  return (
    INTEREST_REGISTRY.getIn([endpointPath, 'status']) === 'ready' ||
    INTEREST_REGISTRY.getIn([endpointPath, 'status']) === 'refreshing' ||
    !resource.isEmpty()
  );
}

export function isResourceValid({
  resourcePath,
  filter,
  include,
  customQuery,
  customHeader
}: ResourceQueryConfigurationType): boolean {
  getResource({
    customHeader,
    customQuery,
    filter,
    include,
    resourcePath
  }); // If we don't have it, initialize it

  const endpointPath = buildEndpoint({resourcePath, filter, include, customQuery});
  return INTEREST_REGISTRY.getIn([endpointPath, 'status']) !== 'error';
}

/**
 * True when a previously requested resource has been invalidated and not yet refetched.
 *
 * Unlike the other predicates this is PASSIVE — no `getResource` call — so it neither
 * initializes/fires a request on cache miss nor consumes the dirty flag (whose refresh
 * `getResource` would kick off as a side effect). Safe to call from change-event
 * subscribers that decide whether to refetch.
 */
export function isResourceStale({
  resourcePath,
  filter,
  include,
  customQuery
}: ResourceQueryConfigurationType): boolean {
  const endpointPath = buildEndpoint({resourcePath, filter, include, customQuery});
  return STATUS_CACHE.getIn([endpointPath, '__dirty'], false) === true;
}

export function isResourceErrorHandled(
  {
    resourcePath,
    filter,
    include,
    customQuery
  }: {
    resourcePath: Array<string>;
    filter: Record<string, unknown>;
    include: string;
    customQuery: Record<string, unknown>;
  },
  handlerId: string
): boolean {
  const endpointPath = buildEndpoint({resourcePath, filter, include, customQuery});
  return INTEREST_REGISTRY.getIn([endpointPath, 'handlers'], Set()).has(handlerId);
}

export function flagResourceErrorHandled(
  {
    resourcePath,
    filter,
    include,
    customQuery
  }: {
    resourcePath: Array<string>;
    filter: Record<string, unknown>;
    include: string;
    customQuery: Record<string, unknown>;
  },
  handlerId: string
) {
  const endpointPath = buildEndpoint({resourcePath, filter, include, customQuery});
  if (
    INTEREST_REGISTRY.has(endpointPath) &&
    INTEREST_REGISTRY.getIn([endpointPath, 'status']) === 'error'
  ) {
    INTEREST_REGISTRY = INTEREST_REGISTRY.updateIn([endpointPath, 'handlers'], Set(), (handlers) =>
      handlers.add(handlerId)
    );
  } else {
    throw new Error(
      `Attempted to flag error as handled for ${endpointPath} but this resource either does not exist or is not an error`
    );
  }
}

export function areAllErrorsHandled(): boolean {
  return getUnhandledErrors().isEmpty();
}

export function getUnhandledErrors(): Map<string, Map<string, unknown>> {
  // Immutable 3.8 types `Map#filter` as returning the wider `Iterable`, but at runtime
  // filtering a Map yields a Map of the same generics.
  return INTEREST_REGISTRY.filter(
    (interest) =>
      interest?.get('status') === 'error' &&
      (interest.get('handlers', Set()) as Set<unknown>).isEmpty()
  ) as Map<string, Map<string, unknown>>;
}

export function buildEndpoint(
  {
    resourcePath,
    filter,
    include,
    customQuery
  }: {
    resourcePath: Array<string>;
    filter?: Record<string, unknown> | null;
    include?: string | null;
    customQuery?: Record<string, unknown> | null;
    // The `= {}` default preserves the original Flow runtime (a no-arg call yields an empty
    // object); the cast keeps the required `resourcePath` contract for real callers.
  } = {} as {resourcePath: Array<string>}
): string {
  const basePath = `${resourcePath.join('/')}`;

  let query = List();

  if (filter) {
    query = query.push(
      qs.stringify(
        {filter},
        {
          encode: false
        }
      )
    );
  }

  if (include) {
    query = query.push(
      qs.stringify(
        {include},
        {
          encode: false
        }
      )
    );
  }

  if (customQuery) {
    query = query.push(
      qs.stringify(customQuery, {
        encode: false
      })
    );
  }

  const queryString = query.join('&');

  return queryString.length > 0 ? `${basePath}?${queryString}` : basePath;
}

// ------- PRIVATE FUNCTIONS -------
function _registerInterest(
  endpointPath: string,
  resourcePath: Array<string>,
  customHeader: Record<string, unknown>
) {
  if (INTEREST_REGISTRY.has(endpointPath)) {
    throw new Error(`Attempted to register interest with id ${endpointPath} which already exists!`);
  }

  const requestFunction = _makeRequest(endpointPath, resourcePath, customHeader);
  const pendingRequest = requestFunction();
  const interestData = Map({
    error: null,
    promise: pendingRequest,
    requestFunction,
    responseData: Map(),
    status: 'pending',
    timestamp: new Date()
  });

  INTEREST_REGISTRY = INTEREST_REGISTRY.set(endpointPath, interestData);
}

function _registerInterestFromServer({
  customHeader,
  endpointPath,
  processedPayload,
  resourcePath,
  meta
}: {
  customHeader: Record<string, unknown>;
  endpointPath: string;
  processedPayload: Record<string, unknown>;
  resourcePath: Array<string>;
  meta?: unknown;
}) {
  const requestFunction = _makeRequest(endpointPath, resourcePath, customHeader);
  const interestData = Map({
    error: null,
    meta,
    promise: Promise.resolve(processedPayload),
    requestFunction,
    responseData: processedPayload,
    status: 'ready',
    timestamp: new Date()
  });

  INTEREST_REGISTRY = INTEREST_REGISTRY.set(endpointPath, interestData);
}

function _refreshCache(interestPath: string, force: boolean = false) {
  // If the request is already pending, do not remake it. This can arise with multiple
  // dependent resources relying on the same query spawning them.
  if (ACTIVE_PENDING_REQUESTS.has(interestPath)) {
    return;
  }

  const timestamp = INTEREST_REGISTRY.getIn([interestPath, 'timestamp'], 0);
  const status = INTEREST_REGISTRY.getIn([interestPath, 'status'], null);
  const requestFunction = INTEREST_REGISTRY.getIn([interestPath, 'requestFunction']);
  const isDirty = STATUS_CACHE.getIn([interestPath, '__dirty'], false);

  if (
    (status === 'ready' && Date.now() - timestamp > CACHE_CONFIG.get('defaultTimeToLive')) ||
    isDirty ||
    force
  ) {
    const refresh = () => {
      const pendingRequest = requestFunction();
      const interestData = Map({
        error: null,
        promise: pendingRequest,
        request: requestFunction,
        status: 'refreshing',
        timestamp: new Date()
      });
      INTEREST_REGISTRY = INTEREST_REGISTRY.mergeDeepIn([interestPath], interestData);
      STATUS_CACHE = STATUS_CACHE.setIn([interestPath, '__dirty'], false);
    };

    if (INVALIDATION_FROZEN) {
      PENDING_INVALIDATIONS = PENDING_INVALIDATIONS.set(interestPath, refresh);
    } else {
      refresh();
    }
  }
}

function generateRequestId(endpointPath: string): string {
  return endpointPath;
}

type GenericRequestQuery = {
  resourcePath: Array<string>;
  filter?: Record<string, unknown> | null;
  include?: string | null;
  customQuery?: Record<string, unknown> | null;
  customHeader?: Record<string, unknown>;
};

type GenericRequestOptions = {
  raw?: boolean;
  abortSignal?: AbortSignal;
  // Opt this request out of the app-global 401/403 toast (App.react.js) — for callers that handle
  // those statuses themselves (e.g. by rendering their own error / empty / access state).
  suppressGlobalErrorToast?: boolean;
};

export function genericCancelableMandarkRequest(
  method: string,
  query: GenericRequestQuery,
  payload?: Record<string, unknown>,
  options: GenericRequestOptions = {raw: false}
): {promise: APIRequestType; cancel: () => void} {
  const fullQuery = buildEndpoint({
    resourcePath: query.resourcePath,
    filter: query.filter,
    include: query.include,
    customQuery: query.customQuery
  });

  const pendingRequest = getRequestAgent()
    [method](getMandarkURL(`/${fullQuery}`)) // oxlint-disable-line no-unexpected-multiline -- computed method access on a fluent chain; reformatting would obscure the request-agent builder pattern
    .timeout(CACHE_CONFIG.get('requestTimeout'))
    .set(BUILD_HEADER(query.customHeader));

  let finished = false;

  const promise = new Promise<CollectionType | ResourceType | null | undefined>(
    (resolve, reject) => {
      pendingRequest.send(payload);
      pendingRequest.end((err, res) => {
        finished = true;
        if (err || (res && !res.ok)) {
          ERROR_HANDLER(err, options.suppressGlobalErrorToast);
          handleMandarkErrorResponse(err, res);
          return reject(err);
        }

        RESPONSE_HANDLER(res);

        if (res.noContent) {
          return resolve(undefined);
        }
        if (options.raw) {
          return resolve(res);
        }
        /*
          If our response is not jsonapi, we simply return the response body as an immutable object (/auth/sign_in),
          otherwise it goes thru the normal response parsing route
        */
        if (res.body && Object.prototype.hasOwnProperty.call(res.body, 'jsonapi')) {
          const processedPayload = _processPayload(
            fromJS(res.body),
            query.resourcePath,
            fullQuery,
            query.customHeader,
            method
          );
          return resolve(processedPayload);
        }
        return res.body ? resolve(fromJS(res.body)) : resolve(undefined);
      });
    }
  );

  // idempotency flag
  let canceled = false;

  const cancel = () => {
    if (!canceled && !finished) {
      canceled = true;
      pendingRequest.abort();
    }
  };

  if (options.abortSignal) {
    options.abortSignal.addEventListener('abort', () => {
      cancel();
    });
  }

  return {
    promise,
    cancel
  };
}

/**
 * Generally speaking, you shouldn't use this directly under any circumstances.
 * A good example of when to use is the practice exam creation steps in `CreateAssigment.store.js`.
 * which has to hit a special endpoint that `save()` isn’t able to account for on its own.
 *
 * If you are looking to create a new instance of a model - the `.save()` method is the way to go.
 * To learn more about creating/deleting model instances and the `.save()` method @see /docs/legacy/genericmodel.md
 *
 * @param method
 * @param query
 * @param payload
 * @param options
 */
export function genericMandarkRequest(
  method: string,
  query: GenericRequestQuery,
  payload: Record<string, unknown>,
  options: GenericRequestOptions = {raw: false}
): APIRequestType {
  const {promise} = genericCancelableMandarkRequest(method, query, payload, options);
  return promise;
}

function _makeRequest(
  endpointPath: string,
  resourcePath: Array<unknown>,
  customHeader: Record<string, unknown>
): () => Promise<void> {
  return async function () {
    const requestId = generateRequestId(endpointPath);
    STATUS_CACHE = STATUS_CACHE.setIn([requestId, '__dirty'], false).setIn(
      [...resourcePath, '__dirty'],
      false
    );
    // The promise registered in ACTIVE_PENDING_REQUESTS must settle only AFTER the
    // response is processed into `responseData` below — not when the raw HTTP response
    // lands. Awaiters (notably getResourcePromise) read `getResource()` the moment this
    // settles; if it settled on the raw response, they'd observe the un-built (raw
    // Immutable Map) cache entry and hand callers a non-model. Keeping the entry until
    // processing completes is what makes `getResourcePromise` reliably return a hydrated
    // model instead of a transient raw Map. It RESOLVES on success and REJECTS on failure
    // (after processing): callers like useAreLatestQueriesReady rely on getResourcePromise
    // REJECTING to detect query errors (403/404 → access/error interstitials), so it must
    // preserve that contract. The `.catch` below keeps the registered promise itself from
    // being flagged as an unhandled rejection when nothing happens to await it.
    let resolveProcessed;
    let rejectProcessed;
    const processedPromise = new Promise((res, rej) => {
      resolveProcessed = res;
      rejectProcessed = rej;
    });
    processedPromise.catch(() => {});
    const pendingGet = new Promise<Map<unknown, unknown>>((resolve, reject) => {
      const pendingGetRequest = getRequestAgent()
        .get(getMandarkURL(`/${endpointPath}`))
        .timeout(CACHE_CONFIG.get('requestTimeout'))
        .set(BUILD_HEADER(customHeader));

      pendingGetRequest.end((err, res) => {
        if (err || (res && (res.status < 200 || res.status > 299))) {
          ERROR_HANDLER(err);
          handleMandarkErrorResponse(err, res);
          return reject(err);
        }
        RESPONSE_HANDLER(res);
        return resolve(fromJS(res.body));
      });
    });

    ACTIVE_PENDING_REQUESTS = ACTIVE_PENDING_REQUESTS.set(requestId, processedPromise);

    try {
      REQUEST_COUNT++;
      const payload = await pendingGet;
      const processedPayload = _processPayload(payload, resourcePath, endpointPath, customHeader);
      INTEREST_REGISTRY = INTEREST_REGISTRY.mergeDeep(
        Map({
          [endpointPath]: Map({
            error: null,
            status: 'ready',
            timestamp: new Date()
          })
        })
      ).setIn([endpointPath, 'responseData'], processedPayload);
      resolveProcessed();
    } catch (err) {
      INTEREST_REGISTRY = INTEREST_REGISTRY.mergeDeep(
        Map({
          [endpointPath]: Map({
            error: err,
            status: 'error',
            timestamp: new Date()
          })
        })
      );
      // Reject after the error entry is written so awaiters (e.g. getResourcePromise →
      // useAreLatestQueriesReady) observe the failure and render error/access states
      // rather than the success path against un-loaded data.
      rejectProcessed(err);
    } finally {
      ACTIVE_PENDING_REQUESTS = ACTIVE_PENDING_REQUESTS.delete(requestId);
      setTimeout(CHANGE_CALLBACK, 0);
    }
  };
}

export function _processPayload(
  payload: Map<unknown, unknown>,
  endpointPath: Array<unknown>,
  interestId: string,
  customHeader: Record<string, unknown> | undefined,
  responseType: string = 'get'
): CollectionType | ResourceType {
  payload
    .getIn(['meta', 'warnings'], List())
    .forEach((warning) =>
      logger.warn(`Query %s has the following warning: %s`, interestId, warning)
    );

  let processedPayload: unknown = null;
  // Process main resource
  if (List.isList(payload.get('data'))) {
    // Process collection
    const data = payload.get('data') as List<Map<string, unknown>>;
    const head = data.first();
    let collectionModel: ModelConstructor | null | undefined;
    if (!head) {
      // default model in the case of an empty list

      const modelNameFromPath = endpointPath.slice(-1)[0];

      collectionModel = getCollectionModel(modelNameFromPath) as
        | ModelConstructor
        | null
        | undefined;
      processedPayload = collectionModel ? new collectionModel() : List();
    } else {
      // Retrieve the model name from the first list element's JSONAPI type attribute
      const resourceType = head.get('type');
      const pluralizedResourceType = getPluralForm(resourceType);
      collectionModel = getCollectionModel(pluralizedResourceType) as
        | ModelConstructor
        | null
        | undefined;
      if (collectionModel) {
        processedPayload = data.reduce(
          (result: List<unknown> = List(), datum: Map<string, unknown> = Map()) => {
            const processedNode = _processNode(
              payload,
              false,
              datum.get('type') as string,
              datum.get('id') as string,
              endpointPath,
              null,
              interestId,
              responseType
            );
            return result.push(processedNode);
          },
          List()
        );

        processedPayload = new collectionModel(processedPayload, true);
      } else {
        processedPayload = data.reduce(
          (result: Map<unknown, unknown> = Map(), datum: Map<string, unknown> = Map()) => {
            const processedNode = _processNode(
              payload,
              false,
              datum.get('type') as string,
              datum.get('id') as string,
              endpointPath,
              null,
              interestId,
              responseType
            );
            return result.set(datum.get('id'), processedNode);
          },
          Map()
        );
      }
    }
  } else {
    processedPayload = _processNode(
      payload,
      false,
      payload.getIn(['data', 'type'], '_UNKNOWN_'),
      payload.getIn(['data', 'id'], '_UNKNOWN_'),
      endpointPath,
      null,
      interestId,
      responseType
    );
  }

  // Avoid storing pure relationship data on parsing a POST payload as a pseudo-full resource (which it is not)
  // ... Unless it already has it, which means it was resolved by something else already and we just want to
  // merge in any extra relationship info
  if (responseType === 'get' || INTEREST_REGISTRY.has(interestId)) {
    INTEREST_REGISTRY = INTEREST_REGISTRY.mergeDeep(
      Map({[interestId]: Map({meta: payload.get('meta')})})
    );
  }

  return processedPayload as CollectionType | ResourceType;
}

function _processNode(
  payload: Map<unknown, unknown>,
  included: boolean,
  resourceType: string,
  id: string,
  endpointPath: Array<unknown> | null | undefined,
  passedMeta: Map<unknown, unknown> | null | undefined,
  interestId: string,
  responseType: string
): Map<unknown, unknown> {
  const mainPath = included ? 'included' : 'data';

  let node: Map<string, unknown>;
  if (List.isList(payload.get(mainPath))) {
    /**
     * Currently, if a query includes two resources of differetnt types but with the same ID (e.g. classrooms_v1 and student_classrooms_v1),
     * the back-end will only return one of the two resources. This can cause our node to not be found. If this is the case, we're going to
     * lean on the similar resource to build our clobbered resource. This is temporary, and will be removed once the back-end merges a fix
     * for the clobbered duplicate resource.
     */
    const mainList = payload.get(mainPath) as List<Map<string, unknown>>;
    const fullMatch = mainList.find(
      (datum: Map<string, unknown> = Map()) =>
        datum.get('id') === id && datum.get('type') === resourceType
    );
    node = (fullMatch ||
      mainList.find((datum: Map<string, unknown> = Map()) => datum.get('id') === id)) as Map<
      string,
      unknown
    >;
  } else {
    node = payload.get(mainPath) as Map<string, unknown>;
  }

  const versionAgnosticResourceType = getVersionAgnosticResourceType(resourceType);

  const relationships = node.get('relationships', Map()) as Map<string, Map<string, unknown>>;
  const resolvedRelationships = relationships.reduce(
    (
      result: Map<unknown, unknown> = Map(),
      relationship: Map<string, unknown> = Map(),
      type: string = ''
    ) => {
      let resolvedResources;
      const nodeRelationshipData = relationship.get('data');
      const isParent = /parent_v[0-9]+/g.test(type);
      if (nodeRelationshipData) {
        if (List.isList(nodeRelationshipData)) {
          const collectionModel = getCollectionModel(type) as ModelConstructor | null | undefined;
          const relationshipList = nodeRelationshipData as List<Map<string, unknown>>;
          resolvedResources = relationshipList.reduce(
            (
              resolvedAcc: List<unknown> = List(),
              relationshipData: Map<string, unknown> = Map()
            ) => {
              const relationshipMeta = relationshipData.has('meta')
                ? Map({
                    relationshipMeta: Map({
                      [versionAgnosticResourceType]: Map({
                        [id]: relationshipData.get('meta')
                      })
                    })
                  })
                : Map();
              return resolvedAcc.push(
                _processNode(
                  payload,
                  true,
                  relationshipData.get('type') as string,
                  relationshipData.get('id') as string,
                  endpointPath,
                  relationshipMeta,
                  interestId,
                  responseType
                )
              );
            },
            List()
          );
          if (collectionModel) {
            resolvedResources = new collectionModel(resolvedResources, true);
          }
        } else {
          const relationshipDataMap = nodeRelationshipData as Map<string, unknown>;
          const relationshipMeta = relationshipDataMap.has('meta')
            ? Map({
                relationshipMeta: Map({
                  [versionAgnosticResourceType]: Map({
                    [id]: relationshipDataMap.get('meta')
                  })
                })
              })
            : Map();
          resolvedResources = _processNode(
            payload,
            true,
            relationship.getIn(['data', 'type']) as string,
            relationship.getIn(['data', 'id']) as string,
            endpointPath,
            relationshipMeta,
            interestId,
            responseType
          );
        }
      }

      const versionAgnosticType = type.replace(/_v[0-9]+$/g, '');
      return !resolvedResources
        ? result
        : result.set(isParent ? 'parent' : versionAgnosticType, resolvedResources);
    },
    Map()
  );

  const populatedMetadataFieldsKey = 'populated_metadata_fields';

  const populatedMetadataFields = node.getIn(['meta', populatedMetadataFieldsKey]);

  const nodeMetaSource = node.get('meta', Map()) as Map<string, unknown>;
  const nodeMeta = nodeMetaSource.reduce(
    (result: Map<unknown, unknown> = Map(), value: unknown = undefined, key: string = '') => {
      if (key !== populatedMetadataFieldsKey && !populatedMetadataFields.includes(key)) {
        return result;
      }
      return result.set(key, value);
    },
    Map()
  );

  const resolvedNode = Map<unknown, unknown>()
    .merge(resolvedRelationships)
    .merge(node.get('attributes') as Map<unknown, unknown>)
    .mergeDeep(Map({meta: nodeMeta}))
    // `passedMeta` may be null/undefined; Immutable's mergeDeep treats those as a no-op at runtime.
    .mergeDeep(passedMeta as Map<unknown, unknown>)
    .set('id', id)
    .set('jsonapi_type', resourceType);

  const pluralResourceType = getPluralForm(resourceType);

  const typeModel = getModel(resolvedNode) as ModelConstructor | null | undefined;

  const processedNode = typeModel ? new typeModel(resolvedNode, true) : resolvedNode;

  setTimeout(() => {
    STATUS_CACHE = STATUS_CACHE.setIn([pluralResourceType, id, '__dirty'], true);
  }, CACHE_CONFIG.get('defaultTimeToLive'));

  STATUS_CACHE = STATUS_CACHE.setIn([pluralResourceType, id, '__interestPath'], endpointPath);

  // A POST / PUT / PATCH response should never be marked as an attached interest, as it is not a persistent interest
  // in the cache, but rather a one-time payload that we have no valid query key for, therefore this is _not_ an
  // attached interest (nor derived ones)
  if (responseType === 'get') {
    STATUS_CACHE = STATUS_CACHE.updateIn(
      [pluralResourceType, id, '__attachedInterests'],
      OrderedSet(),
      (ai) => ai.add(interestId)
    ).updateIn([interestId, '__derivedResources'], Set(), (dr) => dr.add([pluralResourceType, id]));
  }

  return processedNode as Map<unknown, unknown>;
}
